import Header from "./Header";
import Footer from "./Footer";
import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: i * 0.1 },
  }),
};

const slideLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const slideRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.88 },
  visible: (i = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.55, ease: "easeOut", delay: i * 0.1 },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: threshold });
  return [ref, isInView];
}

function About() {
  const [showMore, setShowMore] = useState(false);

  const [heroRef, heroInView] = useScrollReveal(0.1);
  const [visionRef, visionInView] = useScrollReveal(0.1);
  const [masterclassRef, masterclassInView] = useScrollReveal(0.2);
  const [problemRef, problemInView] = useScrollReveal(0.1);
  const [whatWeDoRef, whatWeDoInView] = useScrollReveal(0.1);
  const [internshipRef, internshipInView] = useScrollReveal(0.1);
  const [foundersRef, foundersInView] = useScrollReveal(0.1);
  const [teamRef, teamInView] = useScrollReveal(0.1);
  const [complianceRef, complianceInView] = useScrollReveal(0.15);

  const scrollRef = useRef(null);
  const scroll = (ref, direction) =>
    ref.current.scrollBy({ left: direction * 350, behavior: "smooth" });

  return (
    <>
      <Header />

      {/* ── Hero / About Section ─────────────────────────────────────────── */}
      <section className="my-6" ref={heroRef}>
        <div className="container">
          <div className="mb-5">
            <div className="row mt-3">
              <motion.div
                className="col-lg-7 d-flex flex-column justify-content-center"
                variants={slideLeft}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
              >
                <div className="mb-3">
                  <span className="text-uppercase text-secondary fw-semibold fs-4 ls-sm">
                    About Us
                  </span>
                  <h2 className="h1 fw-semibold ls-sm text-gold lh-1">
                    SLN's Company Profile
                  </h2>
                  <div className="mt-n3 pt-2">
                    <img
                      src="/assets/images/line-shap.webp"
                      width="230"
                      className="mt-2"
                    />
                  </div>
                </div>
                <p className="mb-3 text-black fs-4 lh-sm">
                  SLN Academy is a professional stock market and financial
                  education platform committed to delivering practical,
                  skill-based, and career-oriented training for students,
                  professionals, and aspiring market participants across India.
                </p>
                <p className="mb-2 fs-4 lh-sm">
                  We focus on structured learning, real-world market
                  understanding, and ethical education, without providing
                  investment advice or trading tips. Our approach is built on
                  transparency, compliance, and long-term skill development.
                </p>
                <div>
                  <Link
                    className="btn btn-outline-dark px-5 btn-md mt-4 rounded-pill mb-lg-0 mb-5"
                    to="/contact"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    Contact SLN
                  </Link>
                </div>
              </motion.div>
              <motion.div
                className="col-lg-5 d-flex justify-content-end"
                variants={slideRight}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                whileHover={{ scale: 1.03 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <img
                  src="/assets/images/stock.jpg"
                  className="object-fit-contain"
                  height="250"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Vision & Mission ─────────────────────────────────────────────── */}
      <section className="mt-6" ref={visionRef}>
        <div className="container">
          <motion.div
            className="bg-dark p-7 pb-8 rounded-5"
            variants={scaleIn}
            initial="hidden"
            animate={visionInView ? "visible" : "hidden"}
          >
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate={visionInView ? "visible" : "hidden"}
              custom={0}
            >
              <span className="text-uppercase text-warning fw-semibold fs-4 ls-sm">
                Our Vision
              </span>
              <p className="mt-2 text-white fs-4 lh-sm fw-light">
                To become India's most trusted stock market education platform,
                delivering practical, job ready financial market training, with
                a strong focus on Tier-2 and Tier-3 cities. <br />
                Our vision is to empower youth with financial literacy,
                employable skills, and self-reliance, aligned with the national
                missions of Digital India and Skill India, while building a
                transparent and regulation-compliant education ecosystem.
              </p>
            </motion.div>

            <hr className="my-5 border-light" />

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate={visionInView ? "visible" : "hidden"}
              custom={2}
            >
              <span className="text-uppercase text-warning fw-semibold fs-4 ls-sm">
                Our Mission
              </span>
              <p className="mt-2 text-white fs-4 lh-sm pb-5 fw-light">
                At SLN Academy, our mission is to:
                <ol>
                  <li>
                    Bridge the gap between theoretical financial education and
                    real-world market application through practical,
                    process-driven learning.
                  </li>
                  <li>
                    Provide affordable, accessible, and certification-backed
                    courses in capital markets, designed for students, working
                    professionals, and job seekers.
                  </li>
                  <li>
                    Promote ethical, transparent, and regulation-aligned
                    education, without offering investment advice, trading tips,
                    or guaranteed returns.
                  </li>
                  <li>
                    Support government initiatives related to employment
                    generation, self-employment, and upskilling through
                    structured, outcome-driven programs.
                  </li>
                  <li>
                    Continuously innovate and expand our curriculum, delivery
                    modes (online & offline), and student support systems to
                    reach underserved geographies and diverse demographics.
                  </li>
                </ol>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Masterclass CTA ───────────────────────────────────────────────── */}
      <section ref={masterclassRef}>
        <div className="container">
          <motion.div
            className="bg-success p-lg-6 p-5 rounded-5 mx-lg-8 mx-3 mt-n8"
            variants={scaleIn}
            initial="hidden"
            animate={masterclassInView ? "visible" : "hidden"}
          >
            <div className="container">
              <div className="row">
                <motion.div
                  className="col-lg-8"
                  variants={slideLeft}
                  initial="hidden"
                  animate={masterclassInView ? "visible" : "hidden"}
                >
                  <h5 className="mb-0 fs-3 fw-normal lh-sm text-light">
                    Free 7-Day Masterclass — Intraday & Option Chain Data
                    Trading
                  </h5>
                  <h2 className="h1 text-white fw-normal ls-sm lh-1">
                    Trusted By <span className="fw-bold">10K+</span> Users
                  </h2>
                  <div className="mt-n4 pt-2">
                    <img
                      src="/assets/images/line-shap.webp"
                      width="230"
                      className="mt-2"
                    />
                  </div>
                </motion.div>
                <motion.div
                  className="col-lg-4 d-flex justify-content-lg-end justify-content-center align-items-center"
                  variants={slideRight}
                  initial="hidden"
                  animate={masterclassInView ? "visible" : "hidden"}
                >
                  <motion.a
                    href="https://www.youtube.com/@SLNAcademyOfficial"
                    target="_blank"
                    className="btn btn-light btn-md px-7 rounded-4 mb-lg-0 mb-5"
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    Subscribe Now
                  </motion.a>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Problem Section ───────────────────────────────────────────────── */}
      <section className="py-6 bg-white" ref={problemRef}>
        <div className="container">
          <motion.div
            className="row text-center d-flex justify-content-center"
            variants={fadeUp}
            initial="hidden"
            animate={problemInView ? "visible" : "hidden"}
          >
            <div className="col-lg-7 col-md-12 px-lg-8 mb-3">
              <h2 className="display-5 fw-semibold text-dark mb-0 ls-sm">
                The Problem We Address
              </h2>
              <p className="lead text-dark opacity-75 lh-1 mt-1">
                Got questions? Find all the answers here about SLN Academy, our
                courses, and enrollment process.
              </p>
            </div>
          </motion.div>
          <div className="row justify-content-center">
            <div className="col-lg-10 pb-lg-0 pb-3 d-flex align-items-center">
              <motion.div
                className="accordion accordion-flush w-100 pe-lg-5 pe-0 me-lg-5 me-0"
                id="accordionFlushExample"
                variants={staggerContainer}
                initial="hidden"
                animate={problemInView ? "visible" : "hidden"}
              >
                {[
                  {
                    id: "flush-collapseOne",
                    collapsed: true,
                    bg: "bg-danger",
                    textColor: "text-white",
                    title: "Lack of Practical Financial Education",
                    body: "Most academic programs focus on theory and fail to teach real-world stock market skills, leaving learners unprepared for practical market environments.",
                  },
                  {
                    id: "flush-collapseThree",
                    collapsed: false,
                    bg: "bg-danger-soft",
                    textColor: "text-dark",
                    title: "Limited Access in Tier-2 & Tier-3 Cities",
                    body: "High-quality stock market education is largely urban-centric, making it inaccessible to students and professionals in smaller cities and towns.",
                  },
                  {
                    id: "flush-collapseTwo",
                    collapsed: true,
                    bg: "bg-danger-soft",
                    textColor: "text-dark",
                    title: "Low Financial Literacy Among Youth",
                    body: "A significant portion of students and professionals lack basic financial planning knowledge and market awareness, affecting career growth and financial decision-making.",
                  },
                  {
                    id: "flush-collapseFour",
                    collapsed: true,
                    bg: "bg-danger-soft",
                    textColor: "text-dark",
                    title: "Misinformation & Unregulated Advice",
                    body: "Many learners fall victim to unverified tips and social media noise, instead of following structured, certified, and compliance-driven learning paths.",
                  },
                ].map(({ id, collapsed, bg, textColor, title, body }, i) => (
                  <motion.div
                    key={id}
                    className={`accordion-item${i > 0 ? " mt-2" : ""}`}
                    variants={fadeUp}
                    custom={i}
                  >
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button px-0 ${bg} px-5 rounded-pill${collapsed ? " collapsed" : ""}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#${id}`}
                        aria-expanded={!collapsed}
                        aria-controls={id}
                      >
                        <h3 className={`fs-4 ${textColor} fw-medium m-0 py-1`}>
                          {title}
                        </h3>
                      </button>
                    </h2>
                    <div
                      id={id}
                      className={`accordion-collapse collapse${!collapsed ? " show" : ""}`}
                      data-bs-parent="#accordionFlushExample"
                    >
                      <div className="accordion-body px-5">
                        <p className="fs-4 fw-light mb-4 text-dark opacity-75 lh-sm">
                          {body}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── What We Do ────────────────────────────────────────────────────── */}
      <section className="pb-5 pt-2" ref={whatWeDoRef}>
        <div className="container">
          <motion.div
            className="row"
            variants={fadeUp}
            initial="hidden"
            animate={whatWeDoInView ? "visible" : "hidden"}
          >
            <div className="col-12 mx-auto">
              <div className="d-flex flex-column gap-2 mb-4">
                <h2 className="h1 mb-0 lh-1 ls-sm">What We Do</h2>
                <p className="m-0 fs-4 lh-1">
                  Empowering your trading journey with expert mentorship,
                  practical tools, and lifelong learning support.
                </p>
                <div className="mt-n3 pt-2">
                  <img
                    src="/assets/images/line-shap.webp"
                    width="230"
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div
            className="row g-2"
            variants={staggerContainer}
            initial="hidden"
            animate={whatWeDoInView ? "visible" : "hidden"}
          >
            {[
              {
                num: 1,
                title: "Stock Market & Capital Market Education",
                desc: "Learn the fundamentals and advanced concepts of stock and capital markets with practical insights.",
              },
              {
                num: 2,
                title: "Futures & Options (F&O) Training",
                desc: "Master derivatives trading strategies with hands-on F&O market analysis.",
              },
              {
                num: 3,
                title: "Certification-Oriented Courses",
                desc: "Prepare for industry-recognized certifications with structured, exam-focused learning.",
              },
              {
                num: 4,
                title: "Skill Development & Career-Focused Programs",
                desc: "Build job-ready financial skills aligned with real-world career opportunities.",
              },
              {
                num: 5,
                title: "Online & Offline Learning Modules",
                desc: "Flexible learning options designed to fit your schedule, anytime and anywhere.",
              },
              {
                num: 6,
                title: "Internship & Industry Exposure (learning-oriented)",
                desc: "Gain practical exposure through guided internships focused on real market learning.",
              },
            ].map(({ num, title, desc }, i) => (
              <motion.div
                key={num}
                className="col-md-4 col-6"
                variants={fadeUp}
                custom={i}
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <div className="card-hover-svg card pb-0 rounded-4 bg-warning-soft card-body shadow-none d-flex flex-column gap-3 text-center h-100 justify-content-between">
                  <div className="my-3">
                    <motion.div
                      className="border border-2 border-primary rounded-circle icon-shape icon-md fs-3 text-primary text-center mb-3"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      {num}
                    </motion.div>
                    <h4 className="mb-0 lh-1">{title}</h4>
                    <p className="text-gray-500 lh-1 mt-2 mb-0">{desc}</p>
                  </div>
                  <div className="bg-success pt-1 mt-2"></div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Internship Section ────────────────────────────────────────────── */}
      <section className="mb-7" ref={internshipRef}>
        <div className="container mt-4">
          <div className="row">
            <motion.div
              className="col-md-12"
              variants={fadeUp}
              initial="hidden"
              animate={internshipInView ? "visible" : "hidden"}
            >
              <span className="text-uppercase text-primary fw-semibold ls-sm fs-3 lh-1">
                Internship & Industry Exposure
              </span>
              <h3 className="fw-normal ls-sm text-dark lh-1 mt-1 m-0">
                SLN Academy facilitates internship opportunities for eligible
                students through industry and broking ecosystem associations,
                helping learners gain real-world exposure to financial market
                operations.
              </h3>
              <p className="text-danger fw-light fs-4 lh-sm mt-3">
                Internships are learning-focused, subject to partner
                availability, and do not guarantee employment or placement.
              </p>
              <div className="mt-n3 mb-2">
                <img
                  src="/assets/images/line-shap.webp"
                  width="230"
                  className="mt-2"
                />
              </div>
            </motion.div>
            <div className="position-relative mt-4 mb-2">
              <button
                onClick={() => scroll(scrollRef, -1)}
                className="position-absolute top-50 start-0 translate-middle-y z-1 btn btn-light rounded-circle shadow d-flex align-items-center justify-content-center"
                style={{ width: 44, height: 44 }}
              >
                <i className="bi bi-chevron-left"></i>
              </button>

              <div
                ref={scrollRef}
                className="d-flex gap-2 overflow-x-scroll"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                <img
                  src="/assets/images/internship/1.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="350"
                  height="350"
                />
                <img
                  src="/assets/images/internship/2.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="290"
                  height="350"
                />
                <img
                  src="/assets/images/internship/5.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="420"
                  height="350"
                />
                <img
                  src="/assets/images/internship/6.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="450"
                  height="350"
                />
                <img
                  src="/assets/images/internship/3.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="400"
                  height="350"
                />
                <img
                  src="/assets/images/internship/4.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="400"
                  height="350"
                />
              </div>

              <button
                onClick={() => scroll(scrollRef, 1)}
                className="position-absolute top-50 end-0 translate-middle-y z-1 btn btn-light rounded-circle shadow d-flex align-items-center justify-content-center"
                style={{ width: 44, height: 44 }}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Founders Section ──────────────────────────────────────────────── */}
      <section className="mb-3" ref={foundersRef}>
        <div className="container mt-4">
          <motion.div
            className="row"
            variants={fadeUp}
            initial="hidden"
            animate={foundersInView ? "visible" : "hidden"}
          >
            <div className="col-md-12">
              <span className="text-uppercase text-primary fw-semibold ls-sm fs-3 lh-1">
                About the Founders
              </span>
              <h3 className="fw-normal ls-sm text-dark lh-1 mt-1 m-0 mb-4">
                SLN Academy is led by professionals with hands-on experience in
                the Indian stock market ecosystem and a strong commitment to
                education-first learning. The leadership team brings together
                practical market exposure, teaching experience, and compliance
                awareness, ensuring that students receive realistic, ethical,
                and regulation-aligned education.
              </h3>
              <span className="text-uppercase text-primary fw-semibold ls-sm fs-3 lh-1">
                Our Team
              </span>
              <h3 className="fw-normal ls-sm text-dark lh-1 mt-1 m-0 mb-4">
                Our team consists of:
                <br />
                <br />
                <ul>
                  <li>Experienced Trainers</li>
                  <li>Academic & Curriculum Developers</li>
                  <li>Student Support & Operations Professionals</li>
                </ul>
                Together, we work to deliver high-quality education, continuous
                student support, and a structured learning experience across
                online and offline platforms.
              </h3>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Team Cards ────────────────────────────────────────────────────── */}
      <section className="mb-7" ref={teamRef}>
        <div className="container">
          <motion.div
            className="mb-2"
            variants={slideLeft}
            initial="hidden"
            animate={teamInView ? "visible" : "hidden"}
          >
            <div className="d-flex bg-light p-5 rounded-5">
              <motion.div
                whileHover={{ scale: 1.04 }}
                transition={{ type: "spring", stiffness: 250 }}
              >
                <img
                  src="/assets/images/team/1.jpeg"
                  width="200"
                  height="250"
                  className="object-fit-cover rounded-5"
                />
              </motion.div>
              <div className="ms-4">
                <h3 className="mb-0 ls-sm">Shipra Singh</h3>
                <p className="fs-4 lh-sm">
                  Post Graduate | Equity Derivatives & Fundamentals (SLN
                  Academy)
                  <br />
                  <br />
                  <span className="text-dark fw-medium">Profile Overview</span>
                  <br />
                  Shipra Singh is a highly qualified and experienced stock
                  market professional with a strong academic background and
                  practical exposure in the Indian financial markets. She holds
                  a Post Graduation (Master's Degree). With over 5 years of
                  hands-on experience in the stock market, she has developed a
                  strong command over Equity Derivatives and Fundamental
                  Analysis, making her a valuable part of the academic and
                  training team at SLN Academy.
                  <br />
                  <br />
                  🎓 Academic Qualifications
                  <br />
                  🎓 Post Graduation (Master's Degree)
                  <br />
                  📈 Professional Experience & Expertise
                  <br />
                  🕒 5+ Years of Experience in the Indian Stock Market
                  <br />
                  📊 Expertise in Equity Markets & Derivatives (F&O)
                  <br />
                  📚 Strong command over Fundamental Analysis, including:
                  <br />
                  <br />
                  Financial statement analysis - Balance Sheet, Profit & Loss,
                  and Cash Flow analysis Company fundamentals & business models
                  Valuation basics and long-term investment concepts.
                  {showMore && (
                    <span>
                      <br />
                      <br />
                      🧠 Ability to explain complex market concepts in a simple
                      and structured manner, especially for beginners and
                      intermediate learners
                      <br />
                      👩‍🏫 Role & Responsibilities at SLN Academy
                      <br />
                      <br />
                      🌱 Teaching Philosophy
                      <br />
                      "Strong fundamentals and discipline matter more than
                      short-term profits."
                      <br />
                      <br />
                      Shipra Singh believes that: Stock market education must be
                      ethical, transparent, and compliance-driven Students
                      should focus on understanding market structure and risk,
                      not shortcuts A solid foundation leads to sustainable
                      careers in financial markets
                      <br />
                      <br />
                      🔒 Compliance & Ethics Commitment
                      <br />
                      ❌ No trading tips or buy/sell recommendations
                      <br />
                      ❌ No guaranteed return claims
                      <br />
                      ✅ Education-only, student-centric teaching approach
                      <br />
                      ⭐ Why Students Learn Better With Her
                      <br />
                      <br />
                      ✔ Strong academic background (Post Graduate)
                      <br />
                      ✔ 5+ years of real market experience
                      <br />
                      ✔ Calm, clear, and structured teaching style
                      <br />✔ Focus on long-term learning and skill development
                    </span>
                  )}
                  <br />
                  <br />
                  <motion.span
                    onClick={() => setShowMore(!showMore)}
                    style={{
                      color: "blue",
                      cursor: "pointer",
                      fontWeight: "500",
                    }}
                    whileHover={{ opacity: 0.75 }}
                  >
                    {showMore ? "Show Less" : "Show More"}
                  </motion.span>
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="mb-2"
            variants={slideRight}
            initial="hidden"
            animate={teamInView ? "visible" : "hidden"}
          >
            <div className="d-flex bg-light p-5 rounded-5">
              <motion.div
                whileHover={{ scale: 1.04 }}
                transition={{ type: "spring", stiffness: 250 }}
              >
                <img
                  src="/assets/images/team/2.jpeg"
                  width="200"
                  height="250"
                  className="object-fit-cover rounded-5"
                />
              </motion.div>
              <div className="ms-4">
                <h3 className="mb-0 ls-sm">Gowardhan Kumar</h3>
                <p className="fs-4 lh-sm">
                  Gowardhan Kumar is a certified financial market professional
                  specializing in Equity Derivatives. He holds the NISM Series
                  VIII (Equity Derivatives certification), along with NISM
                  Series V-A (Mutual Fund Distribution) and NISM Series XXI-A
                  (PMS Distribution) certifications.
                  <br />
                  <br />
                  With a strong academic background in Finance (PGDM - Finance)
                  and practical experience in equity and derivatives markets, he
                  focuses on simplifying complex Futures & Options concepts and
                  connecting theory with real-market application. His training
                  emphasizes structured strategy building, risk management,
                  hedging techniques, and disciplined market participation,
                  enabling learners to develop clarity and confidence in
                  derivatives.
                  <br />
                  <br />
                  🎓 Academic & Professional Credentials
                  <br />
                  🎓 PGDM – Finance
                  <br />
                  📜 NISM Series VIII – Equity Derivatives
                  <br />
                  📜 NISM Series V-A – Mutual Fund Distribution
                  <br />
                  📜 NISM Series XXI-A – PMS Distribution
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Compliance Section ────────────────────────────────────────────── */}
      <section className="mb-7" ref={complianceRef}>
        <div className="container mt-4">
          <motion.div
            className="row"
            variants={fadeUp}
            initial="hidden"
            animate={complianceInView ? "visible" : "hidden"}
          >
            <div className="col-md-12">
              <span className="text-uppercase text-primary fw-semibold ls-sm fs-3 lh-1">
                Regulatory & Compliance Commitment
              </span>
              <motion.h3
                className="fw-normal ls-sm text-dark lh-1 mt-1 m-0"
                variants={fadeUp}
                initial="hidden"
                animate={complianceInView ? "visible" : "hidden"}
                custom={1}
              >
                <ul>
                  <li>
                    Operated by <u>Swinglearn Network LLP</u>
                  </li>
                  <li>
                    NSE Registered Authorized Person (AP) under{" "}
                    <u>Choice Equity Broking Pvt. Ltd.</u>
                  </li>
                  <li>Fully aligned with SEBI and Exchange guidelines</li>
                  <li>
                    Zero-Tip Policy – No investment advice, no buy/sell
                    recommendations
                  </li>
                </ul>
              </motion.h3>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default About;
